import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@talentmesh/ui/components/dialog';
import { useEffect, useState } from 'react';
import DialogCard from './Components/DialogCard';
import DialogHeader from './Components/DialogHeader';
import DialogList from './Components/DialogList';
import DialogTemplate from './Components/DialogTemplate';
import DialogCardRejectBody from './Components/Reject/DialogCardRejectBody';
import DialogCardRejectTitle from './Components/Reject/DialogCardRejectTitle';
import RejectActions from './Components/Reject/RejectActions';
import RejectIcon from './Components/Reject/RejectIcon';
import { DialogEnum, useActionDialogContext } from './Context/ActionDialogContext';
import { useAccountSettings } from '@/api/useAccountSettings';
import { useCompanyContext } from '@/Context/CompanyContext';
import { useUserContext } from '@/Context/UserContext';
import RoutePath from '@/Routing/RoutePath';
import useTalentMeshHistory from '@/Routing/useTalentMeshHistory';
import UIStrings from '@/Utils/UIStrings';

interface RejectDialogProps {
    assessmentName: string;
}

function RejectDialog({ assessmentName }: RejectDialogProps) {
    const history = useTalentMeshHistory();
    const { data: accountSettings } = useAccountSettings();
    const { user } = useUserContext();
    const { currentCompanyProfile } = useCompanyContext();
    const { selectedCandidates, isDialogOpen, hideDialog } = useActionDialogContext();

    const [name, setName] = useState('');

    useEffect(() => {
        setName(selectedCandidates.length > 1 ? UIStrings.ApplicantNameVariable : selectedCandidates[0]?.name);
    }, [selectedCandidates]);

    const closeDialog = () => {
        hideDialog(DialogEnum.Reject);
    };

    return (
        <Dialog open={isDialogOpen(DialogEnum.Reject)} onOpenChange={(open) => !open && closeDialog()}>
            <DialogContent className="flex max-h-10/12 max-w-7xl flex-row gap-0 overflow-auto p-0 sm:rounded-2xl">
                <DialogTitle className="sr-only">{UIStrings.Reject}</DialogTitle>
                <DialogDescription className="sr-only">{UIStrings.RejectDialogNote}</DialogDescription>

                <div className="flex w-1/2 flex-col gap-5 p-5 pt-0">
                    <DialogHeader title={UIStrings.Reject} badgeClassName="bg-percentile-very-low" />
                    {selectedCandidates.length === 1 && (
                        <DialogCard
                            dialogTitle={<DialogCardRejectTitle name={name} />}
                            dialogBody={<DialogCardRejectBody name={name} />}
                            icon={<RejectIcon />}
                            dialogCardContainerClassName="h-58"
                        />
                    )}
                    {selectedCandidates.length > 1 && <DialogList title={UIStrings.RejectMultipleApplicants} />}

                    <RejectActions />
                </div>

                <DialogTemplate
                    dialogTitle={UIStrings.PreviewRejectionEmail}
                    note={UIStrings.RejectDialogNote}
                    {...accountSettings.rejectEmailTemplate}
                    candidateName={name}
                    recruiterName={`${user.firstName} ${user.lastName}`}
                    companyName={currentCompanyProfile.companyName}
                    recruitmentName={assessmentName}
                    closeDialog={closeDialog}
                    editEmailTemplate={() => {
                        history.push(RoutePath.AccountSettingsEmailTemplates);
                    }}
                    includeRecruiterName={false}
                />
            </DialogContent>
        </Dialog>
    );
}

export default RejectDialog;
